import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Dumbbell, Zap, Target, ArrowRight, Lock, RefreshCw } from "lucide-react";
import AccountNavbar from "./account/AccountNavbar";
import BottomNav from "../components/ui/BottomNav";

const API = process.env.REACT_APP_BACKEND_URL;

export default function MijnSchemaPage() {
  const { user: authUser, logout } = useAuth();
  const user = authUser;
  const navigate = useNavigate();

  const [bedrijfSchemas, setBedrijfSchemas]   = useState([]);
  const [customSchemas,  setCustomSchemas]    = useState([]);
  const [schemas,        setSchemas]          = useState([]);
  const [loading,        setLoading]          = useState(true);

  const isBedrijfsSporter = user?.role === "sporter";
  const isSchemaOnly      = user?.plan === "schema";

  const loadData = useCallback(async () => {
    if (!user?.token) return;
    const headers = { Authorization: `Bearer ${user.token}` };
    try {
      const [bedrijfRes, customRes, schemaRes] = await Promise.all([
        fetch(`${API}/api/sporter/schemas`,     { headers }),
        fetch(`${API}/api/my-custom-schemas`,   { headers }),
        fetch(`${API}/api/my-schemas`,          { headers }),
      ]);
      if (bedrijfRes.ok) { const d = await bedrijfRes.json(); setBedrijfSchemas(d.schemas || []); }
      if (customRes.ok)  { const d = await customRes.json();  setCustomSchemas(d.schemas || []); }
      if (schemaRes.ok)  { const d = await schemaRes.json();  setSchemas(d.schemas || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  if (!user) { navigate("/"); return null; }

  const handleLogout = () => { logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <AccountNavbar onLogout={handleLogout} />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6 pb-28">
        {/* Header */}
        <div className="mb-6">
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">
            {isBedrijfsSporter ? "Jouw Sportschool" : "Persoonlijk"}
          </p>
          <h1 className="text-2xl font-black text-white">Trainingsschema's</h1>
          <p className="text-zinc-500 text-sm mt-0.5">
            {isBedrijfsSporter
              ? "Schema's toegewezen door jouw trainer"
              : "Jouw persoonlijke trainingsschema's"}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-zinc-600">
            <RefreshCw size={20} className="animate-spin mr-2" />
            <span className="text-sm">Laden...</span>
          </div>
        ) : (
          <>
            {/* ── Bedrijfsschema's — voor sporters ── */}
            {isBedrijfsSporter && (
              bedrijfSchemas.length > 0 ? (
                <div className="space-y-4 mb-6">
                  {bedrijfSchemas.map((s) => (
                    <div key={s.id} className="bg-[#111] border border-[#C9A84C]/12 rounded-2xl overflow-hidden">
                      <div className="flex items-center gap-3 px-5 py-4 border-b border-white/5">
                        <div className="w-10 h-10 rounded-xl bg-[#C9A84C]/10 flex items-center justify-center shrink-0">
                          <Dumbbell size={18} className="text-[#C9A84C]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-bold text-base truncate">{s.naam}</h3>
                          <p className="text-zinc-500 text-xs mt-0.5">
                            {[s.doel, s.niveau, s.trainingsdagen ? `${s.trainingsdagen}x per week` : ""].filter(Boolean).join(" · ")}
                          </p>
                        </div>
                        {s.map_naam && (
                          <span className="text-zinc-600 text-xs px-2 py-1 bg-white/5 rounded-lg shrink-0">{s.map_naam}</span>
                        )}
                      </div>
                      {s.dagen && s.dagen.length > 0 && (
                        <div className="p-3 flex flex-wrap gap-2">
                          {s.dagen.map((dag, i) => (
                            <button
                              key={i}
                              onClick={() => navigate(`/workout-custom/${s.id}/${i}`)}
                              data-testid={`mijn-schema-start-${s.id}-dag-${i}`}
                              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#C9A84C]/12 hover:bg-[#C9A84C]/22 border border-[#C9A84C]/25 text-[#C9A84C] text-sm font-bold transition-all hover:scale-[1.02] active:scale-95"
                            >
                              <Zap size={12} />
                              {dag.naam || `Dag ${i + 1}`}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-[#C9A84C]/8 border border-[#C9A84C]/15 flex items-center justify-center mb-4">
                    <Dumbbell size={28} className="text-zinc-700" />
                  </div>
                  <p className="text-zinc-400 font-semibold mb-1">Nog geen schema's toegewezen</p>
                  <p className="text-zinc-600 text-sm">Je trainer wijst binnenkort een schema toe.</p>
                </div>
              )
            )}

            {/* ── Custom schema's — B2C ── */}
            {!isBedrijfsSporter && customSchemas.length > 0 && (
              <div className="space-y-3 mb-6">
                <p className="text-zinc-400 text-xs font-semibold uppercase tracking-widest">Persoonlijke schema's</p>
                {customSchemas.map((s) => (
                  <div key={s.id} className="bg-[#111] border border-[#C9A84C]/15 rounded-2xl p-5 flex items-start gap-4 hover:border-[#C9A84C]/30 transition-colors">
                    <div className="w-10 h-10 rounded-xl bg-[#C9A84C]/10 flex items-center justify-center shrink-0">
                      <Target size={18} className="text-[#C9A84C]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-bold text-sm mb-0.5 truncate">{s.naam}</h3>
                      <p className="text-zinc-500 text-xs">
                        {[s.doel, s.niveau, s.trainingsdagen ? `${s.trainingsdagen}x per week` : "", s.weken ? `${s.weken} weken` : ""].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    <button
                      onClick={() => navigate(`/custom-schema/${s.id}`)}
                      data-testid={`mijn-schema-custom-${s.id}`}
                      className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-xl text-black shrink-0"
                      style={{ background: "#C9A84C" }}
                    >
                      <ArrowRight size={11} /> Bekijken
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* ── Gekochte schema's — B2C ── */}
            {!isBedrijfsSporter && schemas.length > 0 && (
              <div className="space-y-3 mb-6">
                <p className="text-zinc-400 text-xs font-semibold uppercase tracking-widest">Gekochte schema's</p>
                {schemas.map((s, i) => (
                  <div key={s.product || i} className="bg-[#111] border border-white/8 rounded-2xl p-5 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#C9A84C]/10 flex items-center justify-center shrink-0">
                      <Dumbbell size={18} className="text-[#C9A84C]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-bold text-sm mb-0.5 truncate">{s.label}</h3>
                      {s.frequency && <p className="text-zinc-500 text-xs">{s.frequency}</p>}
                    </div>
                    <button
                      onClick={() => navigate(s.product?.startsWith("hyrox-") ? `/hyrox/${s.product}` : `/schema/${s.product}`)}
                      data-testid={`mijn-schema-bought-${i}`}
                      className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-xl border border-[#C9A84C]/40 text-[#C9A84C] shrink-0 hover:bg-[#C9A84C]/8 transition-colors"
                    >
                      <ArrowRight size={11} /> Bekijken
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* ── Leeg — B2C ── */}
            {!isBedrijfsSporter && customSchemas.length === 0 && schemas.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-2xl bg-white/4 border border-white/8 flex items-center justify-center mb-4">
                  <Lock size={28} className="text-zinc-700" />
                </div>
                <p className="text-zinc-400 font-semibold mb-1">Nog geen schema's</p>
                <p className="text-zinc-600 text-sm mb-5">Bestel een trainingsschema via onze webshop.</p>
                <button
                  onClick={() => { navigate("/"); setTimeout(() => document.querySelector("#aanbod")?.scrollIntoView({ behavior: "smooth" }), 100); }}
                  data-testid="browse-schemas-cta-btn"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-black"
                  style={{ background: "#C9A84C" }}
                >
                  <Dumbbell size={14} /> Bekijk aanbod
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
